import React, { Fragment, useState } from "react"
import Slider from "react-slick"

import Sub from "~layout/sub"
import aside from "~content/products/aside"

import { HeadingIcon } from "~styled"
import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import "./css/QoqoloAccordion.css"
import "./css/DuduTestimonials.css"

const crumbs = [
  { text: "Products", to: "/products" },
  { text: "Dudu Chinese" },
]

const adaptiveProgrammeItems = [
  {
    title: "Adaptive Learning",
    description:
      "DuDu's intelligent system adjusts to each child's reading level, ensuring they are always challenged appropriately without feeling overwhelmed.",
  },
  {
    title: "Analytics",
    description:
      "Progress tracking tools provide insights into reading habits and comprehension, helping parents and educators monitor growth and identify areas for improvement.",
  },
  {
    title: "Interactive Games",
    description:
      "Fun, game-based activities reinforce vocabulary and sentence structures, turning learning into an enjoyable experience.",
  },
  {
    title: "Professional Narration",
    description:
      "Each e-book features clear audio narration, aiding in pronunciation and listening skills.",
  },
  {
    title: "Pinyin Support",
    description:
      "Integrated Pinyin annotations assist learners in understanding pronunciation and tones.",
  },
  {
    title: "Rewards System",
    description:
      "A built-in rewards system motivates children to continue learning by earning points and badges for their achievements.",
  },
]

const faqItems = [
  {
    title: "What is Dudu Chinese?",
    description:
      "Dudu Chinese is an engaging, adaptive reading platform designed to help children aged 6 and above learn Mandarin. It offers a vast library of over 1,000 interactive e-books, each featuring professional narration, Hanyu Pinyin, and concise dictionary explanations. The platform tailors book recommendations to each child's reading level, ensuring a personalised learning experience.",
  },
  {
    title: "How does Dudu determine my child's reading level?",
    description:
      "Upon starting, Dudu administers a placement test that assesses your child's reading proficiency. Based on the results, the app recommends e-books that align with their current level, gradually increasing in complexity as their skills improve. This adaptive approach ensures that learning is both challenging and achievable.",
  },
  {
    title: "Can my child use Dudu offline?",
    description:
      "Yes, Dudu supports offline usage. Users can download e-books while connected to the internet and access them later without an active connection. This feature is particularly useful for learning on the go or in areas with limited internet access.",
  },
  {
    title: "What features does Dudu offer to enhance learning?",
    description: (
      <Fragment>
        <p>Dudu provides a comprehensive suite of features to support language acquisition:</p>
        <ul>
          <li>Interactive e-books with over 1,000 titles across diverse topics.</li>
          <li>Professional narration to support pronunciation and listening skills.</li>
          <li>Hanyu Pinyin support to assist reading and pronunciation.</li>
          <li>In-built dictionary access for quick word meanings.</li>
          <li>Adaptive learning paths with personalised book recommendations.</li>
          <li>Interactive games and quizzes that reinforce reading, listening, and speaking skills.</li>
        </ul>
        <p>These features work together to create an immersive and effective learning environment.</p>
      </Fragment>
    ),
  },
  {
    title: "Is Dudu suitable for beginners in Chinese?",
    description:
      "Absolutely. Dudu is designed to cater to learners at various proficiency levels, including beginners. The platform's gradual progression and supportive features make it an excellent choice for those starting their Mandarin learning journey.",
  },
  {
    title: "How can I get started with Dudu?",
    description:
      "To begin, visit our website to explore the platform and sign up. Dudu is accessible on both iOS and Android devices, allowing for flexible learning across different platforms. For schools or educational institutions interested in integrating Dudu into their curriculum, contact CommonTown directly for more information.",
  },
]

const testimonialItems = [
  {
    name: "Jie En",
    role: "Mum of Jonas, Primary 1",
    avatarImage: require("~images/products/Dudu/Client-Testimonial-Joan.png").default,
    description:
      "My son is very weak in Mandarin and lost interest in reading Chinese books over time due to his inability to sound out the words independently. I found out about Dudu through a friend's Instagram post and decided to give it a try.",
    // link: "/products/dudu/chinese-ebooks/",
  },
  {
    name: "Teacher Jessy Tu",
    role: "International Education Expert",
    avatarImage: require("~images/products/Dudu/client-testimonial.jpg").default,
    description:
      "I really like the dialogic questions provided by Dudu. After finishing the books, I don't need to hassle to check my students' reading comprehension. So, to all teachers, this is a great teaching resource.",
    // link: "/products/dudu/chinese-ebooks/",
  },
  {
    name: "Mrs May Tan",
    role: "Mum of Gordon and Brandon, Primary 6 and Primary 5",
    avatarImage: require("~images/products/Dudu/Client-Testimonial-May Tan.png").default,
    description:
      "Both Gordon and Brandon do not have general interest in reading, especially Chinese books, since young. My husband and I would try to borrow books from the library occasionally but the boys hardly completed the books.",
    // link: "/products/dudu/chinese-ebooks/",
  },
]

const TestimonialArrow = ({ className, onClick, direction }) => (
  <button
    type="button"
    className={className}
    onClick={onClick}
    aria-label={direction === "next" ? "Next testimonial" : "Previous testimonial"}
  >
    <span aria-hidden="true">{direction === "next" ? "\u2192" : "\u2190"}</span>
  </button>
)

const DuduPage = () => {
  const getAccordionId = title =>
    `dudu-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`

  const [openItems, setOpenItems] = useState(() => [
    getAccordionId(adaptiveProgrammeItems[0].title),
    getAccordionId(faqItems[0].title),
  ])
  const [isTestimonialSliderReady, setIsTestimonialSliderReady] = useState(false)

  const toggleItem = id => {
    setOpenItems(current =>
      current.includes(id)
        ? current.filter(itemId => itemId !== id)
        : [...current, id]
    )
  }

  const renderAccordionDescription = description =>
    typeof description === "string" ? <p>{description}</p> : description

  const renderAccordion = items => (
    <div className="product-accordion">
      {items.map(item => {
        const itemId = getAccordionId(item.title)
        const isOpen = openItems.includes(itemId)
        const panelId = `${itemId}-panel`

        return (
          <div className={`product-accordion-item${isOpen ? " is-open" : ""}`} key={itemId}>
            <button
              type="button"
              className="product-accordion-toggle"
              onClick={() => toggleItem(itemId)}
              aria-expanded={isOpen}
              aria-controls={panelId}
            >
              <h3>{item.title}</h3>
              <span className="product-accordion-symbol" aria-hidden="true" />
            </button>

            <div id={panelId} className={`product-accordion-panel${isOpen ? " is-open" : ""}`}>
              {renderAccordionDescription(item.description)}
            </div>
          </div>
        )
      })}
    </div>
  )

  const testimonialSettings = {
    infinite: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: true,
    dots: false,
    speed: 500,
    onInit: () => setIsTestimonialSliderReady(true),
    onReInit: () => setIsTestimonialSliderReady(true),
    prevArrow: <TestimonialArrow direction="prev" />,
    nextArrow: <TestimonialArrow direction="next" />,
    responsive: [
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  }

  const content = (
    <div className="dudu-page">
      <HeadingIcon>
        <img src={require("~images/products/dudu.png").default} alt="Dudu" title="Dudu" />
      </HeadingIcon>
      <h1>Learn Chinese. Love Chinese.</h1>
      <p>To master a language, read more. Dudu helps you read and improve Chinese painlessly.</p>

      <h3 className="para-h3_ bookmark-item_">Challenges of Learning Mandarin Through Traditional Methods</h3>
      <p className="para-content_">
        After years of collaborating with educators, parents, and learners, we have identified that the most significant challenge in learning Chinese as a second language is the monotonous and uninspiring textbooks and lessons. This often results in children developing a negative attitude towards the language.
      </p>

      <h3 className="para-h3_ bookmark-item_">A Mandarin Learning App That Engages Children</h3>
      <p className="para-content_">
        However, with Dudu, you can rest assured that your child will not have to endure another tedious Mandarin learning experience. We offer a solution that is both engaging and effective, ensuring that your child enjoys the process of learning Chinese.
      </p>

      <h3 className="para-h3_ bookmark-item_">Learn to Read Chinese With Ease Through Interactive E-Books</h3>
      <p className="para-content_">
        Dudu is designed to cultivate your child's love for Chinese with over 1,000 colorful and interactive Chinese e-books covering a wide range of topics, from history to science, from Chinese idioms to stories. Each e-book features quick and concise dictionary explanations for every word and professional narration for every sentence. Dudu's unique features include an adaptive online placement test that assesses your child's reading competency, and an automated reading program that recommends level-appropriate books as the child progresses through the levels.
      </p>

      <h3 className="para-h3_ bookmark-item_">Fun Chinese Games and Online Activities for Kids</h3>
      <p className="para-content_">
        Dudu also offers many games and online speaking activities to improve your child's reading, listening, and speaking abilities in a fun environment. Our reading system has been proven to be effective in helping children of all ages learn Chinese as a second language better and faster. It is widely used in schools and homes worldwide and is highly regarded by educators and parents alike.
      </p>

      <h3 className="para-h3_ bookmark-item_">Aligned With HSK/YCT Levels and Trusted Chinese Textbooks</h3>
      <p className="para-content_">
        Dudu is aligned with HSK/YCT levels and popular Chinese L2 textbooks such as Chinese Made Easy (轻松学汉语) and Huanle Huoban (欢乐伙伴). Contact our Dudu education specialists to learn how you can enhance your child's learning with Dudu and these programs and textbooks.
      </p>

      <div className="product-features">
        <div className="product-feature-item">
          <img src={require("~images/products/Dudu/dudu-feature1.png").default} className="img-responsive margin-auto block" alt="e-Books" title="Dudu's extensive collection of over 1000 e-books" />
          <h3 className="para-h3_ mg-btm-sm_">Learn Chinese Online With Over 1,000 E-Books</h3>
          <p>Dudu's extensive collection of over 1,000 e-books is tailored to match learners' abilities and interests, making it easier for learners to find suitable books.</p>
        </div>
        <div className="product-feature-item">
          <img src={require("~images/products/Dudu/dudu-feature7.png").default} className="img-responsive margin-auto block" alt="Dudu reading framework" title="Dudu reading framework" />
          <h3 className="para-h3_ mg-btm-sm_">Adaptive Mandarin Reading App Aligned With HSK/YCT</h3>
          <p>Dudu reading framework is designed with level 0-12 by our experienced education researchers. We employed AI algorithms to align with HSK/YCT and other widely used Chinese L2 textbooks.</p>
        </div>
        <div className="product-feature-item">
          <img src={require("~images/products/Dudu/dudu-feature3.png").default} className="img-responsive margin-auto block" alt="Dudu's reading interface" title="Dudu's reading interface" />
          <h3 className="para-h3_ mg-btm-sm_">Learn to Read Chinese With Ease Through Smart Tools</h3>
          <p>Dudu's reading interface is equipped with a comprehensive dictionary and professional narration, allowing learners to immerse themselves in the books without any interruption.</p>
        </div>
        <div className="product-feature-item">
          <img src={require("~images/products/Dudu/dudu-feature5.png").default} className="img-responsive margin-auto block" alt="Dudu's Interactive Games" title="Dudu's Interactive Games" />
          <h3 className="para-h3_ mg-btm-sm_">Chinese Games for Kids That Make Learning Fun</h3>
          <p>Dudu offers various interactive games that reinforce reading, listening, and speaking skills, making learning an enjoyable experience for learners.</p>
        </div>
      </div>

      <br />
      <div className="product-benefits_">
        <h2 className="para-h2_ text-capitalise_">Learn Chinese for Kids: Dudu's Adaptive Reading Programme</h2>
        {renderAccordion(adaptiveProgrammeItems)}
      </div>

      <br />
      <div className="product-review_">
        <h2 className="para-h2_ text-capitalise_">What Teachers and Students Have to Say</h2>
      </div>

      <div className={`dudu-testimonial-carousel${isTestimonialSliderReady ? " slider-ready" : ""}`}>
        <Slider {...testimonialSettings}>
          {testimonialItems.map(item => (
            <div className="dudu-testimonial-slide" key={item.name}>
              <div className="dudu-testimonial-card">
                <div className="dudu-testimonial-quote-mark">
                  <span aria-hidden="true">❞</span>
                </div>
                <div className="dudu-testimonial-avatar">
                  {item.avatarImage ? (
                    <img src={item.avatarImage} alt={item.name} />
                  ) : (
                    <span>{item.avatarText}</span>
                  )}
                </div>
                <div className="dudu-testimonial-copy">
                  <p>
                    {item.description}{" "}
                    {item.link && item.link != '' && (
                      <a href={item.link} title={`Read more from ${item.name}`}>
                      Read More...
                    </a>)}
                  </p>
                </div>
                <div className="dudu-testimonial-meta">
                  <strong>{item.name}</strong>
                  <span>{item.role}</span>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>

      <br />
      <h2 className="para-h2_ text-capitalise_">Your One-Stop, Mandarin-Learning App</h2>
      <p className="text-center">
        <a href="https://dudu.town" title="Visit Dudu Chinese">
          <img src={require("~images/products/Dudu/dudu.png").default} alt="Dudu" title="Dudu" className="margin-auto img-responsive block" />
          Visit Dudu Chinese
        </a>
      </p>

      <h2 className="contactus-info">Discover the best app to learn mandarin</h2>
      <p>For more information or sales enquiries, email us at <a href="mailto:biz@commontown.com">biz@commontown.com</a> or call us at <a href="tel:6848-8900">6848-8900</a>.</p>

      <h2 className="para-h2_ text-capitalise_">Frequently Asked Questions</h2>
      {renderAccordion(faqItems)}
    </div>
  )

  return <Sub title="Dudu Chinese" seo_name="Dudu Chinese" crumbs={crumbs} content={content} aside={aside} />
}

export default DuduPage
